import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import {
  fetchDevices,
  removeDevice,
  fetchDeviceDetails,
  saveDeviceSettings,
} from "../../../controller/settingsController/activeDeviceController";
import {
  fetchEmailNotificationSetting,
  saveEmailNotificationSetting,
} from "../../../controller/settingsController/securitySettingsController";
import { getStoredDeviceHeaders } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { useToast } from "../../../contexts/ToastContext";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import PermissionBrokerModelController from "../../../controller/model/PermissionBrokerModelController";
import "../../css/activeDevices.css";

export default function ActiveDevices() {
  const { t } = useTranslation("mobile");
  const { success: showSuccess, error: showError } = useToast();
  const [user, setUser] = useState(null);
  const [devices, setDevices] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [revokeAll, setRevokeAll] = useState(false);
  const [revokingAll, setRevokingAll] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailSaving, setEmailSaving] = useState(false);

  const currentDeviceId = getStoredDeviceHeaders()?.["device-id"] ?? null;
  const customerPermController = useMemo(
    () => new PermissionCustomerModelController(user),
    [user]
  );
  const brokerPermController = useMemo(
    () => new PermissionBrokerModelController(user),
    [user]
  );
  const userType = user?.TYPE ?? user?.type ?? user?.userType;
  const userIsBroker =
    userType === 2 || userType === 3 || userType === "BROKER" || userType === "IB";

  const brokerPermissionOptions = useMemo(
    () => [
      {
        key: PermissionBrokerModelController.PER__CAN_DEPOSIT_CLIENT,
        label: "Deposit Only Agent Clients",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_WITHDRAWAL_CLIENT,
        label: "Withdraw Only Agent Clients",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_ENABLE_TRADING_ACCOUNT__ACCOUNT,
        label: "Enable Account",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_ENABLE_TRADING_ACCOUNT__TRADING,
        label: "Enable Trading Account",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_SEND_NOTIFICATION,
        label: "Send Notification",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_VIEW_CLIENT,
        label: "View Client",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_VIEW_CLIENT_TRADING_HISTORY,
        label: "disabled View Client Trading History",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_COLLECT_CASHIN,
        label: "Collect Cashin",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_COLLECT_CASH_OUT,
        label: "Collect Cashout",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_UPDATE_LEVERAGE,
        label: "Update Leverage",
      },
    ],
    []
  );

  const customerPermissionOptions = useMemo(
    () => [
      {
        key: PermissionCustomerModelController.PER__CAN_DEPOSIT_BY_CARD,
        label: "Deposit by Card",
      },
      { key: PermissionCustomerModelController.PER__CAN_CASH_OUT, label: "Cash Out" },
      {
        key: PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_MT5,
        label: "Internal Transfer from MT5 to MT5",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_USD_MT5,
        label: "Internal Transfer from USD to MT5",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_USD,
        label: "Internal Transfer from MT5 to USD",
      },
      { key: PermissionCustomerModelController.PER__CAN_EXCHANGE, label: "Exchange" },
      {
        key: PermissionCustomerModelController.PER__CAN_DEPOSIT_DEMO,
        label: "Deposit Demo Account",
      },
      { key: PermissionCustomerModelController.PER__CAN_CASH_IN, label: "Cash In" },
      {
        key: PermissionCustomerModelController.PER__MANAGEMENT_REAL_ACCOUNT,
        label: "Manage Real Account",
      },
      {
        key: PermissionCustomerModelController.PER__ADD_REAL_ACCOUNT,
        label: "Add Real Account",
      },
      {
        key: PermissionCustomerModelController.PER__MANAGEMENT_DEMO_ACCOUNT,
        label: "Manage Demo Account",
      },
      {
        key: PermissionCustomerModelController.PER__ADD_DEMO_ACCOUNT,
        label: "Add Demo Account",
      },
      {
        key: PermissionCustomerModelController.PER__SEND_MESSAGE,
        label: "Send Message",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_UPDATE_LEVERAGE,
        label: "Update Leverage",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_VIEW_PENDING_REQUEST,
        label: "View Pending Request",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_VIEW_HISTORY,
        label: "View History",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_VIEW_ACCOUNT_HISTORY,
        label: "disabled View Account History",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_VIEW_TOTAL_VOLUME_ACCOUNTS,
        label: "disabled Total Volume Account",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_DOWNLOAD_HISTORY_PDF,
        label: "disabled Download History Pdf",
      },
    ],
    []
  );

  const editableBrokerOptions = useMemo(
    () =>
      brokerPermissionOptions.filter((item) =>
        brokerPermController.permissionsList?.includes(item.key)
      ),
    [brokerPermController.permissionsList, brokerPermissionOptions]
  );

  const editableCustomerOptions = useMemo(
    () =>
      customerPermissionOptions.filter((item) =>
        customerPermController.permissionsList?.includes(item.key)
      ),
    [customerPermController.permissionsList, customerPermissionOptions]
  );

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch (_) {}
  }, []);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchDevices();
      const code = Number(result?.code);
      if (code === AppConstants.responseOk) {
        setIsOwner(result.isOwner);
        setDevices(result.devices);
      } else {
        setDevices([]);
      }
    } catch {
      showError("Error", "Failed to load devices");
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  useEffect(() => {
    const loadEmailSetting = async () => {
      setEmailLoading(true);
      try {
        const result = await fetchEmailNotificationSetting();
        if (result.code === AppConstants.responseOk) {
          setEmailEnabled(result.emailEnabled);
        } else {
          showError(
            t("error", { defaultValue: "Error" }),
            result.message ||
              t("failed_to_load_email_notification_setting", {
                defaultValue: "Failed to load email notification setting.",
              })
          );
        }
      } catch (error) {
        showError(
          t("error", { defaultValue: "Error" }),
          error?.message ||
            t("failed_to_load_email_notification_setting", {
              defaultValue: "Failed to load email notification setting.",
            })
        );
      } finally {
        setEmailLoading(false);
      }
    };

    loadEmailSetting();
  }, [showError, t]);

  const handleEmailToggle = async (checked) => {
    setEmailEnabled(checked);
    setEmailSaving(true);
    try {
      const result = await saveEmailNotificationSetting(checked);
      if (result.code === AppConstants.responseOk) {
        showSuccess(
          t("success", { defaultValue: "Success" }),
          t("email_notification_setting_updated", {
            defaultValue: "Email notification setting updated.",
          })
        );
      } else {
        setEmailEnabled((prev) => !prev);
        showError(
          t("error", { defaultValue: "Error" }),
          result.message ||
            t("failed_to_update_email_notification_setting", {
              defaultValue: "Failed to update email notification setting.",
            })
        );
      }
    } catch (error) {
      setEmailEnabled((prev) => !prev);
      showError(
        t("error", { defaultValue: "Error" }),
        error?.message ||
          t("failed_to_update_email_notification_setting", {
            defaultValue: "Failed to update email notification setting.",
          })
      );
    } finally {
      setEmailSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const data = await removeDevice(deleteTarget);
      const code = Number(data?.CODE);
      if (code === AppConstants.responseOk) {
        showSuccess("Success", "Device removed successfully");
        await loadDevices();
      } else {
        showError("Error", "Failed to remove device");
      }
    } catch {
      showError("Error", "Failed to remove device");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const openEditModal = async (device) => {
    setEditTarget(device.ID);
    setEditLoading(true);
    setEditForm(null);
    try {
      const result = await fetchDeviceDetails(device.ID);
      if (Number(result?.code) === AppConstants.responseOk && result?.device) {
        const selectedDevice = result.device;
        const isCurrentDevice =
          String(selectedDevice.DEVICE_ID ?? device.DEVICE_ID) === String(currentDeviceId);
        const permissionObject =
          typeof selectedDevice.PERMISSION === "string"
            ? (() => {
                try {
                  return JSON.parse(selectedDevice.PERMISSION);
                } catch {
                  return {};
                }
              })()
            : selectedDevice.PERMISSION && typeof selectedDevice.PERMISSION === "object"
              ? selectedDevice.PERMISSION
              : {};
        const brokerDevicePermissions = PermissionBrokerModelController.fromDevice({
          PERMISSION: permissionObject,
        }).permissionsList;
        const customerDevicePermissions = PermissionCustomerModelController.fromDevice({
          PERMISSION: permissionObject,
        }).permissionsList;

        setEditForm({
          id: selectedDevice.ID ?? device.ID,
          permissionObject,
          brokerPermissions: Array.isArray(brokerDevicePermissions)
            ? [...brokerDevicePermissions]
            : [],
          customerPermissions: Array.isArray(customerDevicePermissions)
            ? [...customerDevicePermissions]
            : [],
          isOwner: selectedDevice.IS_OWNER === 1,
          isActive: selectedDevice.IS_ACTIVE === 1,
          receiveNotification: selectedDevice.RECEIVE_NOTIFICATION === 1,
          canEdit: result.isOwner === true,
          isCurrentDevice,
          name: selectedDevice.DEVICE_MODEL || device.DEVICE_MODEL || "Device",
        });
      } else {
        showError("Error", "Failed to load device details");
        setEditTarget(null);
      }
    } catch {
      showError("Error", "Failed to load device details");
      setEditTarget(null);
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditModal = () => {
    if (savingEdit) return;
    setEditTarget(null);
    setEditLoading(false);
    setEditForm(null);
  };

  const updateEditField = (field, value) => {
    setEditForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const toggleDevicePermission = (section, permissionKey, enabled) => {
    const targetField =
      section === "BROKER" ? "brokerPermissions" : "customerPermissions";
    setEditForm((prev) => {
      if (!prev) return prev;
      const currentList = Array.isArray(prev[targetField]) ? prev[targetField] : [];
      const hasPermission = currentList.includes(permissionKey);
      let nextList = currentList;
      if (enabled && !hasPermission) {
        nextList = [...currentList, permissionKey];
      }
      if (!enabled && hasPermission) {
        nextList = currentList.filter((item) => item !== permissionKey);
      }
      return { ...prev, [targetField]: nextList };
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm || savingEdit) return;
    setSavingEdit(true);
    try {
      const response = await saveDeviceSettings({
        DEVICE_ID: editForm.id,
        PERMISSION: JSON.stringify({
          ...(editForm.permissionObject || {}),
          BROKER: editForm.brokerPermissions || [],
          CUSTOMER: editForm.customerPermissions || [],
        }),
        IS_OWNER: !!editForm.isOwner,
        IS_ACTIVE: !!editForm.isActive,
        RECEIVE_NOTIFICATION: !!editForm.receiveNotification,
      });
      const code = Number(response?.CODE);
      if (code === AppConstants.responseOk) {
        showSuccess("Success", "Device updated successfully");
        closeEditModal();
        await loadDevices();
      } else {
        showError("Error", "Please try again");
      }
    } catch {
      showError("Error", "Please try again");
    } finally {
      setSavingEdit(false);
    }
  };

  const formatDate = (raw) => {
    if (!raw) return "-";
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return raw;
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return raw;
    }
  };

  const getDeviceIcon = (device) => {
    const source = `${device?.OS ?? ""} ${device?.DEVICE_MODEL ?? ""}`.toLowerCase();
    if (source.includes("ipad") || source.includes("tablet")) return "fa-tablet-screen-button";
    if (
      source.includes("iphone") ||
      source.includes("ios") ||
      source.includes("android") ||
      source.includes("phone") ||
      source.includes("mobile")
    )
      return "fa-mobile-screen-button";
    if (source.includes("mac") || source.includes("book")) return "fa-laptop";
    if (source.includes("windows") || source.includes("linux") || source.includes("desktop"))
      return "fa-desktop";
    return "fa-display";
  };

  const totalCount = devices.length;
  const activeCount = devices.filter((d) => d.IS_ACTIVE === 1).length;
  const inactiveCount = totalCount - activeCount;

  const filters = [
    { key: "all", label: "All", count: totalCount },
    { key: "active", label: "Active", count: activeCount },
    { key: "inactive", label: "Inactive", count: inactiveCount },
  ];

  const filteredDevices = devices.filter((device) => {
    if (activeFilter === "active" && device.IS_ACTIVE !== 1) return false;
    if (activeFilter === "inactive" && device.IS_ACTIVE === 1) return false;
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;
    return (
      `${device.DEVICE_MODEL ?? ""} ${device.OS ?? ""} ${device.DEVICE_ID ?? ""}`
        .toLowerCase()
        .includes(query)
    );
  });

  const otherDevices = devices.filter(
    (d) => String(d.DEVICE_ID) !== String(currentDeviceId)
  );

  const handleRevokeAll = async () => {
    if (revokingAll) return;
    setRevokingAll(true);
    try {
      let failed = 0;
      for (const device of otherDevices) {
        try {
          const data = await removeDevice(device.ID);
          if (Number(data?.CODE) !== AppConstants.responseOk) failed += 1;
        } catch {
          failed += 1;
        }
      }
      if (failed === 0) {
        showSuccess("Success", "All other devices were signed out");
      } else {
        showError("Error", `Failed to remove ${failed} device(s)`);
      }
      await loadDevices();
    } finally {
      setRevokingAll(false);
      setRevokeAll(false);
    }
  };

  return (
    <div className="active-devices-page">
      <Sidebar user={user} />
      <div className="active-devices-content">
        <div className="active-devices-inner">
          <h1 className="active-devices-title">Active Devices</h1>

          <div className="active-devices-stats">
            <div className="active-stat-card">
              <div className="active-stat-card-top">
                <span className="active-stat-card-label">Total Devices</span>
                <span className="active-stat-card-icon active-stat-card-icon--neutral">
                  <i className="fa-solid fa-mobile-screen-button" />
                </span>
              </div>
              <span className="active-stat-card-value">{totalCount}</span>
              <span className="active-stat-card-hint">Across all platforms</span>
            </div>

            <div className="active-stat-card">
              <div className="active-stat-card-top">
                <span className="active-stat-card-label">Active Now</span>
                <span className="active-stat-card-icon active-stat-card-icon--success">
                  <i className="fa-solid fa-circle-check" />
                </span>
              </div>
              <span className="active-stat-card-value active-stat-card-value--success">
                {activeCount}
              </span>
              <span className="active-stat-card-hint">Online right now</span>
            </div>

            <div className="active-stat-card">
              <div className="active-stat-card-top">
                <span className="active-stat-card-label">Inactive</span>
                <span className="active-stat-card-icon active-stat-card-icon--muted">
                  <i className="fa-solid fa-circle-minus" />
                </span>
              </div>
              <span className="active-stat-card-value">{inactiveCount}</span>
              <span className="active-stat-card-hint">Not in use</span>
            </div>
          </div>

          <div className="active-devices-toolbar">
            <div className="active-devices-filters">
              {filters.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  className={`active-devices-filter${
                    activeFilter === filter.key ? " active-devices-filter--on" : ""
                  }`}
                  onClick={() => setActiveFilter(filter.key)}
                >
                  {filter.label} ({filter.count})
                </button>
              ))}
            </div>
            <div className="active-devices-search">
              <i className="fa-solid fa-magnifying-glass" />
              <input
                type="text"
                placeholder="Search devices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="active-devices-grid">
            {loading ? (
              Array.from({ length: 6 }).map((_, index) => (
                <div
                  className="active-device-card active-device-card--skeleton"
                  key={`device-skeleton-${index}`}
                >
                  <div className="active-device-card-top">
                    <span className="active-device-skeleton active-device-skeleton--avatar" />
                    <div className="active-device-card-heading">
                      <span className="active-device-skeleton active-device-skeleton--title" />
                      <span className="active-device-skeleton active-device-skeleton--sub" />
                      <span className="active-device-skeleton active-device-skeleton--status" />
                    </div>
                  </div>
                  <div className="active-device-card-details">
                    <span className="active-device-skeleton active-device-skeleton--line" />
                    <span className="active-device-skeleton active-device-skeleton--line" />
                    <span className="active-device-skeleton active-device-skeleton--line" />
                  </div>
                  <div className="active-device-card-footer">
                    <span className="active-device-skeleton active-device-skeleton--badge" />
                    <span className="active-device-skeleton active-device-skeleton--action" />
                  </div>
                </div>
              ))
            ) : filteredDevices.length === 0 ? (
              <div className="active-devices-empty">No devices found</div>
            ) : (
              filteredDevices.map((device) => {
                const isCurrent = String(device.DEVICE_ID) === String(currentDeviceId);
                const deviceIsOwner = device.IS_OWNER === 1;
                const deviceIsActive = device.IS_ACTIVE === 1;

                return (
                  <div
                    key={device.ID}
                    className={`active-device-card${
                      isCurrent ? " active-device-card--current" : ""
                    }`}
                  >
                    <div className="active-device-card-top">
                      <span className="active-device-card-avatar">
                        <i className={`fa-solid ${getDeviceIcon(device)}`} />
                      </span>
                      <div className="active-device-card-heading">
                        <span className="active-device-card-name">
                          {device.DEVICE_MODEL || "-"}
                        </span>
                        <span className="active-device-card-os">{device.OS || "-"}</span>
                        <span
                          className={`active-device-card-status${
                            deviceIsActive ? " active-device-card-status--on" : ""
                          }`}
                        >
                          <span className="active-device-card-dot" />
                          {deviceIsActive ? "Active now" : "Inactive"}
                        </span>
                      </div>
                      {isCurrent && (
                        <span className="active-device-card-badge active-device-card-badge--current">
                          This Device
                        </span>
                      )}
                    </div>

                    <div className="active-device-card-details">
                      <div className="active-device-card-detail">
                        <i className="fa-solid fa-desktop" />
                        <span>{device.OS || "-"}</span>
                      </div>
                      <div className="active-device-card-detail">
                        <i className="fa-solid fa-fingerprint" />
                        <span>{device.DEVICE_ID || "-"}</span>
                      </div>
                      <div className="active-device-card-detail">
                        <i className="fa-solid fa-clock" />
                        <span>First seen: {formatDate(device.CREATED_AT)}</span>
                      </div>
                    </div>

                    <div className="active-device-card-footer">
                      {deviceIsOwner ? (
                        <span className="active-device-card-badge active-device-card-badge--owner">
                          Owner
                        </span>
                      ) : (
                        <span className="active-device-card-footer-spacer" />
                      )}
                      {isOwner && (
                        <div className="active-device-card-actions">
                          <button
                            type="button"
                            className="active-device-card-link active-device-card-link--edit"
                            onClick={() => openEditModal(device)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="active-device-card-link active-device-card-link--revoke"
                            disabled={isCurrent}
                            onClick={() => setDeleteTarget(device.ID)}
                          >
                            {isCurrent ? "Sign Out" : "Revoke"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="active-devices-email-box">
            {emailLoading ? (
              <div className="active-devices-email-loading">
                {t("loading", { defaultValue: "Loading" })}...
              </div>
            ) : (
              <div className="active-devices-email-row">
                <div className="active-devices-email-text">
                  <span className="active-devices-email-title">
                    {t("receive_email_notifications", {
                      defaultValue: "Receive Email Notifications",
                    })}
                  </span>
                  <span className="active-devices-email-hint">
                    {t("enable_or_disable_email_updates", {
                      defaultValue: "Enable or disable email updates for account activity.",
                    })}
                  </span>
                </div>
                <label
                  className="active-devices-email-switch"
                  aria-label={t("email_notifications", {
                    defaultValue: "Email notifications",
                  })}
                >
                  <input
                    type="checkbox"
                    checked={emailEnabled}
                    onChange={(e) => handleEmailToggle(e.target.checked)}
                    disabled={emailSaving}
                  />
                  <span className="active-devices-email-slider" />
                </label>
              </div>
            )}
          </div>

          {isOwner && otherDevices.length > 0 && (
            <div className="active-devices-revoke-all">
              <div className="active-devices-revoke-all-text">
                <span className="active-devices-revoke-all-title">
                  Sign out all other devices
                </span>
                <span className="active-devices-revoke-all-hint">
                  This will immediately revoke access from all devices except your current one.
                </span>
              </div>
              <button
                type="button"
                className="active-devices-revoke-all-btn"
                onClick={() => setRevokeAll(true)}
                disabled={revokingAll}
              >
                <i className="fa-solid fa-right-from-bracket" />
                {revokingAll ? "Revoking..." : "Revoke All Others"}
              </button>
            </div>
          )}
        </div>
      </div>

      {deleteTarget !== null && (
        <div className="active-devices-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="active-devices-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="active-devices-dialog-title">Delete Device</h3>
            <p className="active-devices-dialog-text">
              Are you sure you want to remove this device? This action cannot be undone.
            </p>
            <div className="active-devices-dialog-actions">
              <button
                type="button"
                className="active-devices-dialog-btn active-devices-dialog-btn--cancel"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                No
              </button>
              <button
                type="button"
                className="active-devices-dialog-btn active-devices-dialog-btn--confirm"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {revokeAll && (
        <div
          className="active-devices-overlay"
          onClick={() => !revokingAll && setRevokeAll(false)}
        >
          <div className="active-devices-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="active-devices-dialog-title">Revoke All Other Devices</h3>
            <p className="active-devices-dialog-text">
              This will immediately sign out {otherDevices.length} device(s) except your
              current one. This action cannot be undone.
            </p>
            <div className="active-devices-dialog-actions">
              <button
                type="button"
                className="active-devices-dialog-btn active-devices-dialog-btn--cancel"
                onClick={() => setRevokeAll(false)}
                disabled={revokingAll}
              >
                Cancel
              </button>
              <button
                type="button"
                className="active-devices-dialog-btn active-devices-dialog-btn--confirm"
                onClick={handleRevokeAll}
                disabled={revokingAll}
              >
                {revokingAll ? "Revoking..." : "Revoke All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editTarget !== null && (
        <div className="active-devices-overlay" onClick={closeEditModal}>
          <div className="active-devices-dialog active-devices-dialog--edit" onClick={(e) => e.stopPropagation()}>
            <h3 className="active-devices-dialog-title">Edit Device</h3>
            {editLoading || !editForm ? (
              <p className="active-devices-dialog-text">Loading device settings...</p>
            ) : (
              <div className="active-devices-dialog-body">
                <p className="active-devices-dialog-subtitle">{editForm.name}</p>
                <div className="active-device-edit-field">
                  <span>Is Owner</span>
                  <label className="active-device-switch">
                    <input
                      type="checkbox"
                      checked={editForm.isOwner}
                      disabled={!editForm.canEdit || editForm.isCurrentDevice || savingEdit}
                      onChange={(e) => updateEditField("isOwner", e.target.checked)}
                    />
                    <span className="active-device-switch-slider" />
                  </label>
                </div>
                <div className="active-device-edit-field">
                  <span>Is Active</span>
                  <label className="active-device-switch">
                    <input
                      type="checkbox"
                      checked={editForm.isActive}
                      disabled={!editForm.canEdit || editForm.isCurrentDevice || savingEdit}
                      onChange={(e) => updateEditField("isActive", e.target.checked)}
                    />
                    <span className="active-device-switch-slider" />
                  </label>
                </div>
                <div className="active-device-edit-field">
                  <span>Receive Notification</span>
                  <label className="active-device-switch">
                    <input
                      type="checkbox"
                      checked={editForm.receiveNotification}
                      disabled={!editForm.canEdit || savingEdit}
                      onChange={(e) => updateEditField("receiveNotification", e.target.checked)}
                    />
                    <span className="active-device-switch-slider" />
                  </label>
                </div>
                {editForm.isCurrentDevice && (
                  <p className="active-devices-dialog-text">
                    Owner and active status cannot be changed for the current device.
                  </p>
                )}
                {!editForm.canEdit && (
                  <p className="active-devices-dialog-text">You do not have permission to edit this device.</p>
                )}
                {userIsBroker && editableBrokerOptions.length > 0 && (
                  <div className="active-device-permissions-group">
                    <h4 className="active-device-permissions-title">Broker Permissions</h4>
                    {editableBrokerOptions.map((permission) => (
                      <div className="active-device-edit-field" key={`broker-${permission.key}`}>
                        <span>{permission.label}</span>
                        <label className="active-device-switch">
                          <input
                            type="checkbox"
                            checked={editForm.brokerPermissions?.includes(permission.key)}
                            disabled={!editForm.canEdit || savingEdit}
                            onChange={(e) =>
                              toggleDevicePermission("BROKER", permission.key, e.target.checked)
                            }
                          />
                          <span className="active-device-switch-slider" />
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                {editableCustomerOptions.length > 0 && (
                  <div className="active-device-permissions-group">
                    <h4 className="active-device-permissions-title">Customer Permissions</h4>
                    {editableCustomerOptions.map((permission) => (
                      <div className="active-device-edit-field" key={`customer-${permission.key}`}>
                        <span>{permission.label}</span>
                        <label className="active-device-switch">
                          <input
                            type="checkbox"
                            checked={editForm.customerPermissions?.includes(permission.key)}
                            disabled={!editForm.canEdit || savingEdit}
                            onChange={(e) =>
                              toggleDevicePermission("CUSTOMER", permission.key, e.target.checked)
                            }
                          />
                          <span className="active-device-switch-slider" />
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="active-devices-dialog-actions">
              <button
                type="button"
                className="active-devices-dialog-btn active-devices-dialog-btn--cancel"
                onClick={closeEditModal}
                disabled={savingEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="active-devices-dialog-btn active-devices-dialog-btn--confirm"
                onClick={handleSaveEdit}
                disabled={editLoading || !editForm || savingEdit || !editForm.canEdit}
              >
                {savingEdit ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
